import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { useState } from "react";
import type { QuickFilter } from "../../types/factor-mimicking";

interface QuickFilterBadgesProps {
  quickFilters: QuickFilter[];
  selectedQuickFilterId: string | null;
  onQuickFilterSelect: (quickFilter: QuickFilter) => void;
  isLoading?: boolean;
  initialVisibleCount?: number;
}

export const QuickFilterBadges = ({
  quickFilters,
  selectedQuickFilterId,
  onQuickFilterSelect,
  isLoading = false,
  initialVisibleCount = 5,
}: QuickFilterBadgesProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine visible filters based on expand state
  const visibleFilters = isExpanded
    ? quickFilters
    : quickFilters.slice(0, initialVisibleCount);

  const hasMore = quickFilters.length > initialVisibleCount;

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-8 w-32 bg-gray-200 rounded-full animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (quickFilters.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No named filters available
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Badges Grid */}
      <div className="flex flex-wrap gap-2">
        {visibleFilters.map((filter) => {
          const isActive = selectedQuickFilterId === filter.SK;

          return (
            <button
              key={filter.SK}
              onClick={() => onQuickFilterSelect(filter)}
            >
              <Badge
                color={isActive ? "info" : "gray"}
                size="sm"
                className={isActive ? 'bg-blue-600 text-white hover:bg-blue-600' : ''}
              >
                {filter.name}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* Expand/Collapse Button */}
      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-2 py-1"
        >
          {isExpanded ? (
            <>
              <HiChevronUp className="w-4 h-4" />
              <span>Show Less</span>
            </>
          ) : (
            <>
              <HiChevronDown className="w-4 h-4" />
              <span>Show All ({quickFilters.length - initialVisibleCount} more)</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};
